import { slotContext } from "./standings";
import {
  type Field,
  gameSlots,
  type Period,
  resolveSlot,
  type ScheduledGame,
  type StateMessage,
  type Team,
} from "./types";

/**
 * "What is on right now" for a tournament, and "what is my squad's next
 * thing" for a person (2026-09-16, the front page's card): pure, from the
 * schedule, the teams, the fields and each field's live state. The Worker
 * reads those and turns a game's zone-less `startAt` into an instant
 * (`epochOf`, the event's timezone); the client picks the state to show.
 *
 * A game is LIVE when its field's state is between the first pull and the
 * final: `pregame` is a game set up and not started, `final` is over. A
 * game is NEXT when it has no result and starts no more than half an hour
 * ago (the bot's rule, D49). A result is RECENT while the game started
 * within the last three hours: long enough for any format, short enough to
 * still be "just played".
 */
export interface NowSide {
  teamId: string | null;
  name: string;
  short: string;
}

export interface LiveGame {
  fieldId: string;
  field: string;
  /** The schedule row the field was set up from; null for an ad-hoc game. */
  gameId: string | null;
  a: NowSide;
  b: NowSide;
  scoreA: number;
  scoreB: number;
  period: Period;
  running: boolean;
}

export interface NextGame {
  gameId: string;
  fieldId: string;
  field: string;
  /** The schedule's zone-less local time, for the label. */
  startAt: string;
  /** Its instant, for the countdown and the order. */
  epoch: number;
  a: NowSide | null;
  b: NowSide | null;
}

export interface RecentResult {
  gameId: string;
  fieldId: string;
  field: string;
  startAt: string;
  epoch: number;
  a: NowSide | null;
  b: NowSide | null;
  scoreA: number;
  scoreB: number;
}

export interface TournamentNow {
  slug: string;
  name: string;
  /** Server time at the read, so a countdown can correct a phone's clock. */
  at: number;
  live: LiveGame[];
  /** The first of `nextGames`. Kept beside it: a page opened before the list
   *  existed reads this field, and must keep getting a game from it. */
  next: NextGame | null;
  /** Every game of the next kickoff, in the fields' own order. Two pitches
   *  start a round together, and a card naming one of them told the other
   *  pitch's teams nothing (owner, 2026-09-19). */
  nextGames: NextGame[];
  recent: RecentResult | null;
}

/** One squad of the person, at one current event. */
export interface SquadNow {
  eventSlug: string;
  eventName: string;
  teamId: string;
  teamShort: string;
  teamName: string;
  at: number;
  live: LiveGame | null;
  next: NextGame | null;
  recent: RecentResult | null;
}

export interface NowInput {
  teams: Team[];
  schedule: ScheduledGame[];
  fields: readonly Field[];
  /** Each field's live state; null where the object has nothing yet. */
  states: readonly { fieldId: string; state: StateMessage | null }[];
  /** A schedule row's instant, or null when the string is not a time. */
  epochOf: (startAt: string) => number | null;
  now: number;
}

const NEXT_GRACE_MS = 30 * 60_000;
const RECENT_MS = 3 * 60 * 60_000;

function side(team: Team | null): NowSide | null {
  return team === null ? null : { teamId: team.id, name: team.name, short: team.short };
}

function fieldName(fields: readonly Field[], id: string): string {
  return fields.find((f) => f.id === id)?.name ?? id;
}

/** The games being played: a field between its first pull and the final. */
export function liveGames(input: Pick<NowInput, "fields" | "states">): LiveGame[] {
  const out: LiveGame[] = [];
  for (const { fieldId, state } of input.states) {
    if (state === null) continue;
    const g = state.state;
    if (g.period === "pregame" || g.period === "final") continue;
    out.push({
      fieldId,
      field: fieldName(input.fields, fieldId),
      gameId: g.scheduleGameId,
      a: { teamId: g.teamA.teamId, name: g.teamA.name, short: g.teamA.short },
      b: { teamId: g.teamB.teamId, name: g.teamB.name, short: g.teamB.short },
      scoreA: g.teamA.score,
      scoreB: g.teamB.score,
      period: g.period,
      running: g.clock.running,
    });
  }
  return out.sort((x, y) => x.field.localeCompare(y.field));
}

function sidesOf(
  game: ScheduledGame,
  ctx: ReturnType<typeof slotContext>,
): { a: NowSide | null; b: NowSide | null } {
  const slots = gameSlots(game);
  return { a: side(resolveSlot(slots.a, ctx)), b: side(resolveSlot(slots.b, ctx)) };
}

function involves(sides: { a: NowSide | null; b: NowSide | null }, teamId: string | undefined): boolean {
  return teamId === undefined || sides.a?.teamId === teamId || sides.b?.teamId === teamId;
}

/** The earliest unplayed game (of one squad, or of anyone) that has not
 *  started more than half an hour ago. */
export function nextKickoff(
  input: Omit<NowInput, "states">,
  teamId?: string,
): NextGame | null {
  const ctx = slotContext(input.teams, input.schedule);
  let best: NextGame | null = null;
  for (const game of input.schedule) {
    if (game.result !== undefined) continue;
    const epoch = input.epochOf(game.startAt);
    if (epoch === null || epoch < input.now - NEXT_GRACE_MS) continue;
    const sides = sidesOf(game, ctx);
    if (!involves(sides, teamId)) continue;
    if (best === null || epoch < best.epoch) {
      best = {
        gameId: game.id,
        fieldId: game.fieldId,
        field: fieldName(input.fields, game.fieldId),
        startAt: game.startAt,
        epoch,
        ...sides,
      };
    }
  }
  return best;
}

/** Every unplayed game that shares the earliest kickoff still ahead (the same
 *  half hour of grace), in the fields' own order: the round that starts next,
 *  not one game picked out of it. */
export function nextKickoffs(input: Omit<NowInput, "states">): NextGame[] {
  const first = nextKickoff(input);
  if (first === null) return [];
  const ctx = slotContext(input.teams, input.schedule);
  const order = (fieldId: string): number => {
    const at = input.fields.findIndex((f) => f.id === fieldId);
    return at === -1 ? input.fields.length : at;
  };
  return input.schedule
    .filter((game) => game.result === undefined && input.epochOf(game.startAt) === first.epoch)
    .map((game) => ({
      gameId: game.id,
      fieldId: game.fieldId,
      field: fieldName(input.fields, game.fieldId),
      startAt: game.startAt,
      epoch: first.epoch,
      ...sidesOf(game, ctx),
    }))
    .sort((x, y) => order(x.fieldId) - order(y.fieldId) || x.field.localeCompare(y.field));
}

/** The latest played game (of one squad, or of anyone) that started within
 *  the last three hours. */
export function recentResult(
  input: Omit<NowInput, "states">,
  teamId?: string,
): RecentResult | null {
  const ctx = slotContext(input.teams, input.schedule);
  let best: RecentResult | null = null;
  for (const game of input.schedule) {
    if (game.result === undefined) continue;
    const epoch = input.epochOf(game.startAt);
    if (epoch === null || epoch > input.now || epoch < input.now - RECENT_MS) continue;
    const sides = sidesOf(game, ctx);
    if (!involves(sides, teamId)) continue;
    if (best === null || epoch > best.epoch) {
      best = {
        gameId: game.id,
        fieldId: game.fieldId,
        field: fieldName(input.fields, game.fieldId),
        startAt: game.startAt,
        epoch,
        ...sides,
        scoreA: game.result.scoreA,
        scoreB: game.result.scoreB,
      };
    }
  }
  return best;
}

export function tournamentNow(
  input: NowInput & { slug: string; name: string },
): TournamentNow {
  const nextGames = nextKickoffs(input);
  return {
    slug: input.slug,
    name: input.name,
    at: input.now,
    live: liveGames(input),
    next: nextGames[0] ?? null,
    nextGames,
    recent: recentResult(input),
  };
}

export function squadNow(
  input: NowInput & {
    eventSlug: string;
    eventName: string;
    teamId: string;
    teamShort: string;
    teamName: string;
  },
): SquadNow {
  const mine = (g: LiveGame): boolean => g.a.teamId === input.teamId || g.b.teamId === input.teamId;
  return {
    eventSlug: input.eventSlug,
    eventName: input.eventName,
    teamId: input.teamId,
    teamShort: input.teamShort,
    teamName: input.teamName,
    at: input.now,
    live: liveGames(input).find(mine) ?? null,
    next: nextKickoff(input, input.teamId),
    recent: recentResult(input, input.teamId),
  };
}

export type SquadState = "live" | "recent" | "next";

/** What the card shows for a squad: the game on the field beats a result
 *  from the hour before, which beats the next kickoff. Null: nothing. */
export function squadState(s: SquadNow): SquadState | null {
  if (s.live !== null) return "live";
  if (s.recent !== null) return "recent";
  if (s.next !== null) return "next";
  return null;
}

/** Of several squads (two clubs, two divisions), the one with the most
 *  pressing thing: live first, then a fresh result, then the soonest next. */
export function pickSquad(squads: readonly SquadNow[]): SquadNow | null {
  const rank: Record<SquadState, number> = { live: 0, recent: 1, next: 2 };
  let best: SquadNow | null = null;
  let bestKey: [number, number] | null = null;
  for (const s of squads) {
    const state = squadState(s);
    if (state === null) continue;
    const key: [number, number] = [rank[state], state === "next" ? (s.next?.epoch ?? 0) : 0];
    if (bestKey === null || key[0] < bestKey[0] || (key[0] === bestKey[0] && key[1] < bestKey[1])) {
      best = s;
      bestKey = key;
    }
  }
  return best;
}

/** The other side of a squad's game, by the squad's id. */
export function opponentOf<S extends { a: NowSide | null; b: NowSide | null }>(
  game: S,
  teamId: string,
): NowSide | null {
  return game.a?.teamId === teamId ? game.b : game.b?.teamId === teamId ? game.a : null;
}

/** A squad's own score first: "us : them", and whether it won. */
export function fromSquad(
  game: { a: NowSide | null; b: NowSide | null; scoreA: number; scoreB: number },
  teamId: string,
): { us: number; them: number; outcome: "won" | "lost" | "drawn" } {
  const home = game.a?.teamId === teamId;
  const us = home ? game.scoreA : game.scoreB;
  const them = home ? game.scoreB : game.scoreA;
  return { us, them, outcome: us > them ? "won" : us < them ? "lost" : "drawn" };
}
